/**
 * The function guide: every entry in the menu bar, resolved to what it does.
 *
 * Home is not a splash screen. It is the one place that answers "what can this
 * software do" in full, so it is built from the same `NAV` tree the menu bar
 * renders — not from a hand-written list that would drift the first time a
 * tool is renamed. If a function exists, it is reachable from the bar, so it
 * is in the guide; `tests/nav-guide.test.mjs` fails the build if anything in
 * the bar has no description here.
 *
 * Three sources of truth, none of them re-typed:
 *   - a tool leaf takes `ToolDef.description` and `ToolDef.status`
 *   - a use-case leaf takes `UseCase.outcome` and `UseCase.unavailable`
 *   - a shell-command leaf takes `ACTION_HELP` below, which is the only thing
 *     written by hand, because an action id carries no prose of its own
 *
 * The state a row shows is the *capability's* state, not this session's:
 * "future" means nobody can run it yet, and is honest on a cold machine with
 * no study open. Whether the current study satisfies a function is decided
 * later, by the launch sheet, against real inputs.
 */

import { NAV, type NavGroup, type NavLeaf, type NavSection } from "./nav.config";
import { toolById } from "@/tools/catalog";
import { USE_CASES } from "@/usecases/catalog";

/** A capability's own state, independent of what is loaded right now. */
export type GuideState = "ready" | "future";

export interface GuideEntry {
  /** Stable within the page; leaves have no id of their own. */
  key: string;
  label: string;
  /** One line: what it does, or what you get. May be empty for a bare toggle. */
  detail: string;
  /** The third menu level, where the leaf has one. */
  submenu?: string;
  state: GuideState;
  /** Why it is not available. Set only when `state` is "future". */
  note?: string;
  /** What runs the row, routed through the launch sheet by `activateLeaf`. */
  leaf: NavLeaf;
  /** Free-text the search box matches, lower-cased. */
  haystack: string;
}

export interface GuideGroup {
  id: string;
  label: string;
  hint?: string;
  entries: GuideEntry[];
}

export interface GuideSection {
  id: string;
  label: string;
  full?: string;
  blurb: string;
  tab?: NavSection["tab"];
  groups: GuideGroup[];
  /** Entries across every group, for counting and searching. */
  count: number;
}

/**
 * Prose for leaves that are shell commands. A tool and a use case each carry
 * their own sentence; an action id like `view.layout.mpr` does not, and the
 * guide would otherwise print a label twice and call it an explanation.
 *
 * Anything missing here renders as the label alone rather than as invented
 * text — a wrong description of a display control is worse than none.
 */
const ACTION_HELP: Record<string, string> = {
  "workspace.new": "Start from nothing: no study, default layout.",
  "workspace.open": "Reopen a saved arrangement of study, views and overlays.",
  "workspace.save": "Remember the current study and layout under a name.",
  "file.preferences": "Engine, default folders, clinical or research mode.",
  "case.close": "Close the study and clear the viewer.",

  "output.open":
    "Reveal the subject's output folder in Finder, exactly as the pipeline wrote it.",

  "ribbon.toggle": "Hide the toolbar for more image, or bring it back.",
  "panel.toggle.left": "The study tree beside the image.",
  "panel.toggle.right": "The inspector beside the image.",
  "panel.toggle.bottom": "Jobs, log, QC and console.",
  "window.fullscreen": "Fill the screen.",

  "dock.show.qc": "The checklist of what a run produced and what is missing.",
  "dock.show.jobs": "Runs in progress and finished, with their timings.",
  "dock.show.log": "The pipeline's own output, line by line.",

  "view.layout.single": "One large viewport.",
  "view.layout.grid": "Four viewports: three planes and a rendering.",
  "view.layout.mpr": "The three planes side by side.",
  "view.layout.compare": "Two volumes in the same plane, crosshairs linked.",
  "view.orientation.axial": "Look from below, the radiological convention.",
  "view.orientation.sagittal": "Look from the side.",
  "view.orientation.coronal": "Look from the front.",
  "view.reset": "Back to the default zoom, pan and slice.",
  "view.overlay.toggle": "Show or hide the mask drawn over the image.",
  "view.opacity.25": "Overlay at a quarter strength: the image underneath stays readable.",
  "view.opacity.50": "Overlay and image weighted equally.",
  "view.opacity.70": "Overlay dominant, the image still visible behind it.",
  "view.opacity.100": "Overlay opaque: the image underneath is hidden where it covers.",
  "view.colormap.gray": "Draw the overlay in greyscale.",
  "view.colormap.warm": "A warm scale — the convention for perfusion maps.",
  "view.colormap.cool": "A cool scale, for a second overlay beside a warm one.",
  "view.colormap.jet": "The rainbow scale. Legible in a screenshot, misleading for a gradient.",
  "view.windowLevel": "Set the grey range by hand.",
  "view.autoContrast": "Set the grey range from the image's own histogram.",
  "view.crosshairs.toggle": "Show or hide the crosshair at the current point.",
  "view.linkViews.toggle": "Move every viewport to the same point at once.",
  "view.goToCoordinate": "Jump to a voxel or millimetre coordinate.",
  "view.zoom": "Scale the image in the viewport.",
  "view.pan": "Move the image within the viewport.",
  "view.snapshot": "Save the viewport as it looks right now to an image file.",
  "bookmark.add": "Mark this point so you can return to it.",

  "dev.engine.setup": "Choose the OpenADS folder and the Python that runs it.",
  "dev.engine.verify": "Re-check the engine and report what is missing.",
  "dev.docker.status": "Whether the Docker fallback is reachable.",
  "dev.docker.image": "Which OpenADS image would be used.",
  "dev.docker.pull": "Download the OpenADS image.",
  "dev.config.yaml": "The YAML the pipeline is about to be handed.",
  "dev.copyShellCommand": "Copy the exact command line, to run it outside the app.",
  "dev.copyDockerCommand": "Copy the equivalent docker run command.",

  "shell.shortcuts": "Every keyboard shortcut.",
  "shell.about": "Version, engine and licences.",
  "shell.commandPalette": "Reach any command by typing its name.",
};

function resolve(leaf: NavLeaf, key: string): GuideEntry | null {
  if (leaf.toolId) {
    const tool = toolById(leaf.toolId);
    if (!tool) return null;
    const label = leaf.label ?? tool.label;
    return {
      key,
      label,
      detail: tool.description,
      submenu: leaf.submenu,
      state: tool.status === "future" ? "future" : "ready",
      note: tool.status === "future" ? (tool.comingSoon ?? "Not wrapped yet.") : undefined,
      leaf,
      haystack: `${label} ${tool.description} ${tool.engine} ${tool.id}`.toLowerCase(),
    };
  }

  if (leaf.useCaseId) {
    const useCase = USE_CASES.find((u) => u.id === leaf.useCaseId);
    if (!useCase) return null;
    const label = leaf.label ?? useCase.title;
    return {
      key,
      label,
      detail: useCase.outcome,
      submenu: leaf.submenu,
      state: useCase.unavailable ? "future" : "ready",
      note: useCase.unavailable,
      leaf,
      haystack: `${label} ${useCase.outcome} ${(useCase.keywords ?? []).join(" ")}`.toLowerCase(),
    };
  }

  if (leaf.action) {
    const label = leaf.label ?? leaf.action;
    const detail = ACTION_HELP[leaf.action] ?? "";
    return {
      key,
      label,
      detail,
      submenu: leaf.submenu,
      state: "ready",
      leaf,
      haystack: `${label} ${detail}`.toLowerCase(),
    };
  }

  return null;
}

function buildGroup(section: NavSection, group: NavGroup): GuideGroup {
  const entries: GuideEntry[] = [];
  group.items.forEach((leaf, i) => {
    const entry = resolve(leaf, `${section.id}/${group.id}/${i}`);
    if (entry) entries.push(entry);
  });
  return { id: group.id, label: group.label, hint: group.hint, entries };
}

/**
 * The whole guide. Help is excluded: "About" and "Keyboard shortcuts" are
 * about the application, not about what it can do to a study, and listing them
 * beside the pipeline would say they are the same kind of thing.
 */
export function buildGuide(): GuideSection[] {
  return NAV.filter((s) => s.id !== "home" && s.id !== "help").map((section) => {
    const groups = section.groups.map((g) => buildGroup(section, g));
    return {
      id: section.id,
      label: section.label,
      full: section.full,
      blurb: section.blurb,
      tab: section.tab,
      groups,
      count: groups.reduce((n, g) => n + g.entries.length, 0),
    };
  });
}

/** Entries whose text matches every whitespace-separated term. */
export function searchGuide(sections: GuideSection[], query: string, limit = 24) {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return [];
  const hits: { section: GuideSection; group: GuideGroup; entry: GuideEntry }[] = [];
  for (const section of sections) {
    for (const group of section.groups) {
      for (const entry of group.entries) {
        if (terms.every((t) => entry.haystack.includes(t))) hits.push({ section, group, entry });
        if (hits.length >= limit) return hits;
      }
    }
  }
  return hits;
}

/** Entries with no sentence of their own — the gap `ACTION_HELP` has to close. */
export function undocumented(sections: GuideSection[]): string[] {
  const out: string[] = [];
  for (const s of sections) {
    for (const g of s.groups) {
      for (const e of g.entries) if (!e.detail.trim()) out.push(`${s.id}/${g.id}/${e.label}`);
    }
  }
  return out;
}
